import { useEffect, useRef, useState } from "react";
import { drawComet } from "../lib/comet";

// Mini preview canvas of a comet skin with a horizontal sweep animation.
// The comet enters from the right edge, arcs across the full canvas
// width (so the long tail sweeps the whole frame), exits the left edge,
// and loops. This shows the full sparkle + starburst trail in motion —
// far more compelling than a still palette swatch.
//
// **Fluid sizing**: the canvas measures its parent width via
// ResizeObserver and renders at the parent's actual width × `size`
// height. This lets cards on mobile (narrow column) shrink the canvas
// proportionally instead of clipping. `aspect="square"` falls back to
// a fixed `size×size` square (used for compact loadout thumbnails).
export default function SkinPreview({ skin, size = 120, aspect = "wide" }) {
  const wrapperRef = useRef(null);
  const ref = useRef(null);
  const rafRef = useRef(null);
  // Measured-width state. For square mode we use `size` directly. For
  // wide mode we let the canvas fill its container so it always reads
  // edge-to-edge inside whatever card it lives in.
  const [measuredW, setMeasuredW] = useState(
    aspect === "square" ? size : Math.round(size * 2.4)
  );

  // ResizeObserver: keep the canvas pixel buffer in sync with the wrapper
  // width. Wide aspect → fluid; square aspect → fixed `size`.
  useEffect(() => {
    if (aspect === "square") {
      setMeasuredW(size);
      return;
    }
    if (typeof ResizeObserver === "undefined") return; // older browsers
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = Math.max(80, Math.min(600, Math.round(entry.contentRect.width)));
        setMeasuredW(w);
      }
    });
    ro.observe(wrapper);
    return () => ro.disconnect();
  }, [aspect, size]);

  const W = measuredW;
  const H = size;

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let mounted = true;
    let lastTs = 0;
    let phase = 0;
    let bob = 0;
    const radius = Math.max(7, H * 0.13);

    const loop = (ts) => {
      if (!mounted) return;
      if (!lastTs) lastTs = ts;
      const dt = Math.min(50, ts - lastTs);
      lastTs = ts;
      phase = (phase + dt / 3000) % 1.0;
      bob += dt / 800;

      ctx.fillStyle = "#05050a";
      ctx.fillRect(0, 0, W, H);
      const vg = ctx.createRadialGradient(W * 0.5, H * 0.55, 0, W * 0.5, H * 0.55, W * 0.7);
      vg.addColorStop(0, "rgba(155,33,255,0.10)");
      vg.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = vg;
      ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = "rgba(255,255,255,0.45)";
      for (let i = 0; i < 18; i++) {
        const sx = (i * 73.13 + skin.id.length * 17) % W;
        const sy = (i * 31.7 + 5) % H;
        const sr = i % 4 === 0 ? 1.4 : 0.7;
        ctx.beginPath();
        ctx.arc(sx, sy, sr, 0, Math.PI * 2);
        ctx.fill();
      }

      // Comet path — keep the head INSIDE the canvas at the trailing
      // moment of the loop (was W*1.15 → -0.15 which clipped on narrow
      // canvases). New range: 0.95 → 0.05 keeps the bright nucleus
      // visible at all times so mobile previews always read clearly.
      const cx = W * (0.95 - phase * 0.9);
      const cy = H * 0.55 + Math.sin(bob) * (H * 0.08);
      const dx = -1;
      const dy = Math.cos(bob) * 0.08;
      const angle = Math.atan2(dy, dx);

      drawComet(ctx, skin, cx, cy, radius, { angle, trail: [], glowBoost: 0.95 });

      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      mounted = false;
      cancelAnimationFrame(rafRef.current);
    };
  }, [skin, W, H]);

  return (
    <div
      ref={wrapperRef}
      className="block bg-[#05050a]"
      style={
        aspect === "square"
          ? { width: size, height: size }
          : { width: "100%", height: H }
      }
    >
      <canvas
        ref={ref}
        width={W}
        height={H}
        className="block border border-white/10 bg-[#05050a]"
        style={{ width: "100%", height: "100%" }}
        data-testid={`skin-preview-${skin.id}`}
      />
    </div>
  );
}
